import { FormEvent, useEffect, useState } from "react";
import { Link, Navigate, useLocation } from "react-router-dom";
import { Loader2, Lock } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAdminAuth } from "@/hooks/useAdminAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const AdminLogin = () => {
  const { loading, session, isAuthorized } = useAdminAuth();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    document.title = "Admin Login | Hadassah Homes";
  }, []);

  if (!loading && session && isAuthorized) {
    const from = (location.state as { from?: string } | null)?.from || "/admin";
    return <Navigate to={from} replace />;
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    const { error: signInError } = await supabase.auth.signInWithPassword({
      email: email.trim(),
      password,
    });
    setSubmitting(false);
    if (signInError) {
      setError("Invalid email or password.");
      toast.error("Sign in failed");
      return;
    }
    toast.success("Signed in");
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#0F1F18] px-5 py-16">
      <div className="w-full max-w-md border border-white/10 bg-white p-8 md:p-10">
        <div className="mb-7">
          <p className="font-body text-[10px] uppercase tracking-[0.28em] text-accent">
            Hadassah Homes
          </p>
          <h1 className="mt-2 font-headline text-3xl text-foreground">Admin Sign In</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Authorized personnel only. Public signup is disabled.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label className="mb-2 block text-xs uppercase tracking-[0.16em] text-muted-foreground">
              Email
            </label>
            <Input
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="rounded-none"
            />
          </div>
          <div>
            <label className="mb-2 block text-xs uppercase tracking-[0.16em] text-muted-foreground">
              Password
            </label>
            <Input
              type="password"
              required
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="rounded-none"
            />
          </div>

          {error ? <p className="text-sm text-destructive">{error}</p> : null}

          <Button
            type="submit"
            variant="gold"
            size="lg"
            disabled={submitting || loading}
            className="w-full rounded-none"
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}
          </Button>

          <div className="flex items-center justify-between text-[11px] text-muted-foreground">
            <Link
              to="/admin/forgot-password"
              className="text-accent hover:text-accent/80"
            >
              Forgot password?
            </Link>
            <span className="inline-flex items-center gap-1.5">
              <Lock className="h-3 w-3" /> Secured by Supabase Auth
            </span>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminLogin;
